/**
 * Session key management for persisting the master key across page refreshes.
 * 
 * Strategy:
 * 1. Generate a random device key and store it in persistent storage
 * 2. Encrypt the master key with the device key
 * 3. Store the encrypted master key in persistent storage
 * 4. On page load, retrieve and decrypt the master key
 * 
 * This approach:
 * - Keeps the master key usable across refreshes without re-entering password
 * - Device key provides minimal protection (better than plaintext)
 * - Logout clears both keys, requiring password re-entry
 * 
 * Storage:
 * - In Electron: Uses file system via IPC (userData) - survives app restarts
 * - In Browser: Uses IndexedDB - survives cache clears
 */

import { encrypt, decrypt } from './encryption';
import { appStorage } from '../appStorage';

const DEVICE_KEY = 'consl_dk';
const ENCRYPTED_MASTER_KEY = 'consl_emk';

/**
 * Generate a 32-byte device key from a UUID.
 * The UUID is stored, and we hash it to get consistent 32 bytes.
 */
async function deriveDeviceKey(deviceKeyStr: string): Promise<Uint8Array> {
  const encoder = new TextEncoder();
  const data = encoder.encode(deviceKeyStr);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(hashBuffer);
}

/**
 * Store the master key encrypted with a device key.
 * 
 * @param masterKey - The master encryption key to store
 */
export async function storeMasterKey(masterKey: Uint8Array): Promise<void> {
  // Generate new device key
  const deviceKeyStr = crypto.randomUUID();
  const deviceKey = await deriveDeviceKey(deviceKeyStr);
  
  // Encode master key as base64 for encryption
  const masterKeyBase64 = btoa(String.fromCharCode(...masterKey));
  
  // Encrypt master key with device key
  const encryptedMasterKey = await encrypt(masterKeyBase64, deviceKey);
  
  // Store both using appStorage (persists correctly in Electron and browser)
  await appStorage.setItem(DEVICE_KEY, deviceKeyStr);
  await appStorage.setItem(ENCRYPTED_MASTER_KEY, encryptedMasterKey);
}

/**
 * Retrieve the master key from storage.
 * 
 * @returns The master key if available, null otherwise
 */
export async function retrieveMasterKey(): Promise<Uint8Array | null> {
  const deviceKeyStr = await appStorage.getItem<string>(DEVICE_KEY);
  const encryptedMasterKey = await appStorage.getItem<string>(ENCRYPTED_MASTER_KEY);
  
  if (!deviceKeyStr || !encryptedMasterKey) {
    return null;
  }
  
  try {
    const deviceKey = await deriveDeviceKey(deviceKeyStr);
    const masterKeyBase64 = await decrypt(encryptedMasterKey, deviceKey);
    return Uint8Array.from(atob(masterKeyBase64), c => c.charCodeAt(0));
  } catch (error) {
    // If decryption fails, clear corrupted data
    console.warn('Failed to retrieve master key, clearing session:', error);
    await clearMasterKey();
    return null;
  }
}

/**
 * Clear the stored master key and device key.
 * Should be called on logout.
 */
export async function clearMasterKey(): Promise<void> {
  await appStorage.removeItem(DEVICE_KEY);
  await appStorage.removeItem(ENCRYPTED_MASTER_KEY);
}

/**
 * Check if a master key is stored.
 */
export async function hasMasterKey(): Promise<boolean> {
  const deviceKeyStr = await appStorage.getItem<string>(DEVICE_KEY);
  const encryptedMasterKey = await appStorage.getItem<string>(ENCRYPTED_MASTER_KEY);
  return !!(deviceKeyStr && encryptedMasterKey);
}
